import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { of, firstValueFrom } from 'rxjs';
import { Incident, deserializeIncident } from '../models/incident';
import { environment } from '../../environments/environment';

export interface CreateIncidentRequest {
    description: string;
    service: string;
    severity: string;
    reporter: string;
    startedAt?: string;
    resolvedAt?: string;
    status: string;
    initialLogMessage?: string;
}

export interface UpdateIncidentRequest {
    incidentId: string;
    description?: string;
    service?: string;
    severity?: string;
    startedAt?: string;
    logMessage?: string;
    updatedBy?: string;
}

export interface ResolveIncidentRequest {
    incidentId: string;
    resolvedAt?: string;
    resolutionMessage?: string;
    resolvedBy?: string;
}

export interface AddLogRequest {
    incidentId: string;
    message: string;
    author: string;
    timestamp?: string;
}

@Injectable({
    providedIn: 'root',
})
export class IncidentService {
    private readonly http = inject(HttpClient);
    private readonly apiUrlOrigin = environment.local
        ? environment.apiUrlOrigin
        : environment.apiUrlOrigin + '/incident-bot';

    getIncidents(): Observable<Incident[]> {
        return this.http.get<unknown[]>(this.apiUrlOrigin + '/api/getincidents').pipe(
            map((data) =>
                data.map((d) =>
                    deserializeIncident(d as Parameters<typeof deserializeIncident>[0]),
                ),
            ),
            catchError((error) => {
                console.error('Could not fetch incidents', error);
                return of([]);
            }),
        );
    }

    async createIncident(request: CreateIncidentRequest): Promise<Incident> {
        const url = this.apiUrlOrigin + '/api/admin/createincident';
        return firstValueFrom(
            this.http.post<Incident>(url, request).pipe(
                catchError((error) => {
                    console.error('Could not create incident', error);
                    throw error;
                }),
            ),
        );
    }

    async updateIncident(request: UpdateIncidentRequest): Promise<Incident> {
        const url = this.apiUrlOrigin + '/api/admin/updateincident';
        return firstValueFrom(
            this.http.put<Incident>(url, request).pipe(
                catchError((error) => {
                    console.error('Could not update incident', error);
                    throw error;
                }),
            ),
        );
    }

    async resolveIncident(request: ResolveIncidentRequest): Promise<Incident> {
        const url = this.apiUrlOrigin + '/api/admin/resolveincident';
        return firstValueFrom(
            this.http.put<Incident>(url, request).pipe(
                catchError((error) => {
                    console.error('Could not resolve incident', error);
                    throw error;
                }),
            ),
        );
    }

    async addLogToIncident(request: AddLogRequest): Promise<Incident> {
        const url = this.apiUrlOrigin + '/api/admin/addlog';
        return firstValueFrom(
            this.http.post<Incident>(url, request).pipe(
                catchError((error) => {
                    console.error('Could not add log to incident', error);
                    throw error;
                }),
            ),
        );
    }
}
